import { useEffect, useMemo, useState } from "react";
import { AnalyticsChartCard, MultiLineChart, StyledPieChart } from "../ChartComponents";
import DashboardMetricsService from "../../services/dashboardMetricsService";
import { useDashboardUI } from "../../context/GlobalContext";
import { buildAnalyticsQueryParams } from "../../helpers/helpers";
// import { useDashboardUI } from "../context/GlobalContext";

/**
 * Merge multiple analytics arrays by date
 */
function mergeAnalyticsByDate(metrics) {
    const map = {};

    metrics.forEach(({ key, source, valueKey }) => {
        source.forEach(item => {
            if (!map[item.date]) {
                map[item.date] = { date: item.date };
            }
            map[item.date][key] = item[valueKey] || 0;
        });
    });

    return Object.values(map).sort(
        (a, b) => new Date(a.date) - new Date(b.date)
    );
}

/**
 * Metric configuration (SOURCE-AWARE)
 */
const METRIC_CONFIG = {
    workspaces_created: {
        source: "workspace",
        metricKey: "workspaces_created",
        chartKey: "workspaces",
        valueKey: "total_workspaces",
        label: "Workspaces",
        color: "#6366F1",
    }
};

const WORKSPACE_USER_KEYS = [
    "single_workspace_users",
    "multi_workspace_users",
];

const WORKSPACE_USER_LABELS = {
    single_workspace_users: "Single workspace users",
    multi_workspace_users: "Multi workspace users",
};

const WORKSPACE_USER_COLORS = [
    "#6366F1", // primary
    "#22C55E", // success
];

function transformWorkspaceUsersToPieData(workspaceUsers = {}) {
    return WORKSPACE_USER_KEYS.map((key, index) => ({
        name: WORKSPACE_USER_LABELS[key],
        value: workspaceUsers?.[key] ?? 0,
        color: WORKSPACE_USER_COLORS[index],
    }));
}


export default function WorkspaceAnalytics() {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const {
        selectedFacilityFilter,
        selectedStartDateFilter,
        selectedEndDateFilter,
    } = useDashboardUI();


    const fetchAnalytics = async () => {
        try {
            setLoading(true);

            const queryParams = buildAnalyticsQueryParams({
                startValue: selectedStartDateFilter,
                endValue: selectedEndDateFilter,
                facilityId: selectedFacilityFilter?.facility_number,
            });

            const [worksapceRes] = await Promise.all([
                DashboardMetricsService.getWorkspaceAnalytics(queryParams),
            ]);

            setAnalytics({
                workspace: worksapceRes.data,
            });
        } catch (err) {
            console.error("Failed to fetch analytics", err);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Fetch analytics (NAMESPACED)
     */
    useEffect(() => {
        fetchAnalytics();
    }, [selectedStartDateFilter, selectedEndDateFilter, selectedFacilityFilter])

    /**
     * Build chart data by metric keys
     */
    const buildChartData = metricKeys => {
        const activeMetrics = metricKeys.map(key => {
            const config = METRIC_CONFIG[key];
            const sourceData =
                analytics?.[config.source]?.[config.metricKey] || [];

            return {
                mergeConfig: {
                    key: config.chartKey,
                    source: sourceData,
                    valueKey: config.valueKey,
                },
                lineConfig: {
                    dataKey: config.chartKey,
                    stroke: config.color,
                    name: config.label,
                },
            };
        });

        return {
            chartData: mergeAnalyticsByDate(
                activeMetrics.map(m => m.mergeConfig)
            ),
            lines: activeMetrics.map(m => m.lineConfig),
        };
    };

    /**
     * Chart datasets
     */
    const workspaceChart = useMemo(
        () =>
            analytics
                ? buildChartData([
                    "workspaces_created",
                ])
                : { chartData: [], lines: [] },
        [analytics]
    );

    const workspaceUsersPieData = useMemo(() => {
        if (!analytics) return [];
        return transformWorkspaceUsersToPieData(
            analytics?.workspace?.workspace_users
        );
    }, [analytics]);

    const workspaceUsersTotal = workspaceUsersPieData.reduce(
        (sum, d) => sum + d.value,
        0
    );


    /**
     * Loading / error states
     */
    if (loading) {
        return (
            <div className="rounded-xl border bg-white p-6 shadow-sm">
                <div className="flex h-[300px] items-center justify-center text-sm text-slate-400">
                    Loading analytics…
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-xl border bg-white p-6 shadow-sm">
                <div className="flex h-[300px] items-center justify-center text-sm text-red-500">
                    Failed to load analytics
                </div>
            </div>
        );
    }

    /**
     * UI
     */
    return (
        <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-2">
            <AnalyticsChartCard
                title="Workspaces Created Over Time"
                subtitle=""
                {...workspaceChart}
            />

            <div className="min-w-0 w-full rounded-xl border bg-white p-6 shadow-sm">
                {workspaceUsersTotal > 0 ? (
                    <div className="flex flex-col">
                        <StyledPieChart
                            data={workspaceUsersPieData}
                            dataKey="value"
                            nameKey="name"
                            colors={workspaceUsersPieData.map((d) => d.color)}
                            height={300}
                            outerRadius={75}
                        />

                        <div className="mt-6 text-sm font-medium text-slate-700 text-center">
                            Total Users:{" "}
                            {analytics?.workspace?.workspace_users?.total_users && (<span className="font-semibold text-slate-900">
                                {analytics?.workspace?.workspace_users?.total_users}
                            </span>)}
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-[300px] text-gray-500">
                        No workspace user data available
                    </div>
                )}
            </div>


        </div>
    );
}
